import React, { useMemo } from "react";

const calculateStats = (matches, division) => {
  const stats = {};

  matches
    .filter((match) => {
      // ✅ Only pool phase (NOT elimination) and right division
      return match.phase !== "elimination" && match.division === division && match.result;
    })
    .forEach((match) => {
      const { team1, team2, result, team1Name, team2Name } = match;

      const team1Display = team1?.players
        ? team1.players.map((p) => p.name).join(" & ")
        : team1Name || "BYE";
      const team2Display = team2?.players
        ? team2.players.map((p) => p.name).join(" & ")
        : team2Name || "BYE";

      if (!stats[team1Display])
        stats[team1Display] = { team: team1Display, wins: 0, scored: 0, conceded: 0 };
      if (!stats[team2Display])
        stats[team2Display] = { team: team2Display, wins: 0, scored: 0, conceded: 0 };

      const t1Score = result.team1 ?? 0;
      const t2Score = result.team2 ?? 0;

      stats[team1Display].scored += t1Score;
      stats[team1Display].conceded += t2Score;

      stats[team2Display].scored += t2Score;
      stats[team2Display].conceded += t1Score;

      if (t1Score > t2Score) stats[team1Display].wins += 1;
      else if (t2Score > t1Score) stats[team2Display].wins += 1;
    });

  return Object.values(stats).map((stat) => ({
    ...stat,
    difference: stat.scored - stat.conceded,
  }));
};



const StatsTable = ({ matches, teams }) => {
  const availableDivisions = useMemo(() => {
    return Object.keys(teams || {}).sort((a, b) => {
      if (a === "division1") return -1;
      if (b === "division1") return 1;
      return a.localeCompare(b);
    });
  }, [teams]);

  const divisionStats = useMemo(() => {
    return availableDivisions.reduce((acc, division) => {
      const stats = calculateStats(matches, division);
      stats.sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.difference - a.difference;
      });
      acc[division] = stats;
      return acc;
    }, {});
  }, [matches, availableDivisions]);

  return (
    <div className="mt-6 w-full flex flex-wrap gap-4 justify-center">
      {availableDivisions.map((division) => (
        <div
          key={division}
          className="w-full md:w-[48%] bg-sky-900 shadow-md rounded"
        >
          <h3 className="text-lg font-bold px-4 pt-4 text-white">
            Classement –{" "}
            {division === "division1" ? "Division 1" : "Division 2"}
          </h3>
          <div className="overflow-x-auto m-2">
            <table className="min-w-full table-auto text-xs md:text-sm text-white">
              <thead className="bg-red-800 text-white">
                <tr>
                  <th className="px-2 py-1 md:px-2 md:py-2 border">#</th>
                  <th className="px-2 py-1 md:px-4 md:py-2 border">Équipe</th>
                  <th className="px-2 py-1 md:px-4 md:py-2 border">Victoires</th>
                  <th className="px-2 py-1 md:px-4 md:py-2 border">Pts marqués</th>
                  <th className="px-2 py-1 md:px-4 md:py-2 border">Pts encaissés</th>
                  <th className="px-2 py-1 md:px-4 md:py-2 border">Diff.</th>
                </tr>
              </thead>
              <tbody>
                {divisionStats[division].map((team, idx) => (
                  <tr key={team.team}>
                    <td className="px-2 py-1 md:px-2 md:py-2 border text-center bg-gray-700 font-bold text-white">
                      {idx + 1}
                    </td>
                    <td className="px-2 py-1 md:px-4 md:py-2 border">
                      {team.team}
                    </td>
                    <td className="px-2 py-1 md:px-4 md:py-2 border text-center">
                      {team.wins}
                    </td>
                    <td className="px-2 py-1 md:px-4 md:py-2 border text-center">
                      {team.scored}
                    </td>
                    <td className="px-2 py-1 md:px-4 md:py-2 border text-center">
                      {team.conceded}
                    </td>
                    <td className="px-2 py-1 md:px-4 md:py-2 border text-center font-bold">
                      {team.difference > 0 ? "+" : ""}
                      {team.difference}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {divisionStats[division].length === 0 && (
              <p className="px-4 py-4 italic text-gray-400 text-center">
                Aucun résultat disponible pour cette division.
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsTable;
